/*
Copyright 2024 New Vector Ltd.

SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-Element-Commercial
Please see LICENSE in the repository root for full details.
*/

import { type TrackReferenceOrPlaceholder } from "@livekit/components-core";
import { animated } from "@react-spring/web";
import { type RoomMember } from "matrix-js-sdk";
import {
  type FC,
  type ComponentProps,
  type ReactNode,
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import classNames from "classnames";
import { VideoTrack } from "@livekit/components-react";
import { Text, Tooltip } from "@vector-im/compound-web";
import { ErrorSolidIcon } from "@vector-im/compound-design-tokens/assets/web/icons";

import {
  tileShape,
  televisionIntensity,
  useSetting,
} from "../settings/settings";
import { AgendaBadge } from "../room/AgendaBadge";
import styles from "./MediaView.module.css";
import { Avatar } from "../Avatar";
import {
  type EncryptionStatus,
  type ParticipantConnectionQuality,
} from "../state/MediaViewModel";
import { RaisedHandIndicator } from "../reactions/RaisedHandIndicator";
import { type ReactionOption } from "../reactions";
import { ReactionIndicator } from "../reactions/ReactionIndicator";
import { RTCConnectionStats } from "../RTCConnectionStats";
import { SideEqualizer } from "./SideEqualizer";
import { useSafeVideoFit } from "./useSafeVideoFit";

// Границы зума демонстрации экрана (CTO 04.09.2026: «зум демонстрации экрана участником»).
// Меньше 1 не нужно — картинка и так вписана целиком через videoFit="contain".
const MIN_ZOOM = 1;
const MAX_ZOOM = 4;
const WHEEL_ZOOM_COEFFICIENT = 0.0025;
// DOM_DELTA_LINE — колесо мыши шлёт дельту в «строчках», а не в пикселях; трекпад обычно
// шлёт DOM_DELTA_PIXEL и коэффициент выше не нужен, но на всякий случай приводим к пикселям.
const WHEEL_LINE_HEIGHT_PX = 16;

/**
 * Очертания экрана кинескопа в долях от размера плитки: стороны выгнуты наружу,
 * середина каждой стороны доходит до края кадра, а углы поджаты внутрь. Скруглением
 * такого не получить — там стороны остаются прямыми (CTO 06 и 07.09.2026).
 */
const CRT_SHAPE =
  "M0.045,0.07 C0.28,-0.012 0.72,-0.012 0.955,0.07 " +
  "C1.012,0.28 1.012,0.72 0.955,0.93 " +
  "C0.72,1.012 0.28,1.012 0.045,0.93 " +
  "C-0.012,0.72 -0.012,0.28 0.045,0.07 Z";

interface Props extends ComponentProps<typeof animated.div> {
  className?: string;
  style?: ComponentProps<typeof animated.div>["style"];
  targetWidth: number;
  targetHeight: number;
  video: TrackReferenceOrPlaceholder | undefined;
  videoFit: "cover" | "contain";
  mirror: boolean;
  /**
   * Позволяет зумить и таскать видео колесом мыши и перетаскиванием (CTO 04.09.2026:
   * зум демонстрации экрана участником). Опционально и по умолчанию выключено — обычная
   * плитка с камерой участника (намного более частый рендер, чем демонстрация экрана)
   * этим не затрагивается.
   */
  zoomable?: boolean;
  member: RoomMember | undefined;
  videoEnabled: boolean;
  unencryptedWarning: boolean;
  encryptionStatus: EncryptionStatus;
  nameTagLeadingIcon?: ReactNode;
  showLocalRecordingIndicator?: boolean;
  displayName: string;
  focusable: boolean;
  primaryButton?: ReactNode;
  raisedHandTime?: Date;
  currentReaction?: ReactionOption;
  raisedHandOnClick?: () => void;
  localParticipant: boolean;
  audioStreamStats?: RTCInboundRtpStreamStats | RTCOutboundRtpStreamStats;
  videoStreamStats?: RTCInboundRtpStreamStats | RTCOutboundRtpStreamStats;
  connectionQuality?: ParticipantConnectionQuality;
  /**
   * Ретро-эквалайзер сбоку от кадра — прыгает, пока участник говорит (CTO 04.09.2026).
   * Без явного значения не показывается: у демонстрации экрана и заглушки без видео
   * своего «говорит» нет.
   */
  speaking?: boolean;
  activeSpeaker?: boolean;
  /**
   * Ретро-оформление: кадр по форме экрана кинескопа. Ставится только плитке с камерой
   * человека. Демонстрация экрана остаётся обычным прямоугольником: на ней читают текст
   * и таблицы, а выпуклые стороны съедают края (CTO 07.09.2026).
   */
  retro?: boolean;
}

export const MediaView: FC<Props> = ({
  ref,
  className,
  style,
  targetWidth,
  targetHeight,
  video,
  videoFit,
  mirror,
  zoomable = false,
  member,
  videoEnabled,
  unencryptedWarning,
  nameTagLeadingIcon,
  showLocalRecordingIndicator = false,
  displayName,
  focusable,
  primaryButton,
  encryptionStatus,
  raisedHandTime,
  currentReaction,
  raisedHandOnClick,
  localParticipant,
  audioStreamStats,
  videoStreamStats,
  connectionQuality,
  speaking,
  activeSpeaker,
  retro = false,
  ...props
}) => {
  // Своя маска на каждую плитку: общая на весь документ пропала бы вместе с кадром,
  // если бы кто-то отрисовал плитку отдельно от приложения.
  // React выдаёт имена вроде «r1» или :r1: — в ссылке url(#…) такие знаки лишние,
  // оставляем только буквы, цифры, дефис и подчёркивание.
  const clipId = `klava-crt-${useId().replace(/[^a-zA-Z0-9_-]/g, "")}`;
  const { t } = useTranslation();
  const connectionQualityLabel = ((): string => {
    switch (connectionQuality) {
      case "excellent":
        return t("video_tile.connection_quality.excellent");
      case "good":
        return t("video_tile.connection_quality.good");
      case "poor":
        return t("video_tile.connection_quality.poor");
      case "lost":
        return t("video_tile.connection_quality.lost");
      default:
        return t("video_tile.connection_quality.unknown");
    }
  })();
  const showConnectionQualityIndicator =
    connectionQuality === "good" ||
    connectionQuality === "poor" ||
    connectionQuality === "lost";

  const [shape] = useSetting(tileShape);
  const [tvIntensity] = useSetting(televisionIntensity);
  const television = retro && shape === "tv";

  const avatarSize = Math.round(Math.min(targetWidth, targetHeight) / 2);

  const bgRef = useRef<HTMLDivElement | null>(null);

  // Обрезка кадра не должна резать человека пополам: стоячее видео с телефона в
  // лежачей плитке теряло почти всё (CTO 11.09.2026). Мерим по самому кадру.
  const mayCrop = useSafeVideoFit(bgRef);
  const effectiveVideoFit = retro
    ? "cover"
    : videoFit === "cover" && !mayCrop
      ? "contain"
      : videoFit;
  const [zoom, setZoom] = useState(1);
  const [translation, setTranslation] = useState({ x: 0, y: 0 });
  const [panning, setPanning] = useState(false);
  const dragOrigin = useRef({
    pointerX: 0,
    pointerY: 0,
    translationX: 0,
    translationY: 0,
  });
  // trackSid меняется при каждой новой демонстрации экрана — старый зум не должен
  // переезжать на неё, иначе первый же кадр покажет обрезанный кусок нового содержимого.
  const trackSid = video?.publication?.trackSid;

  useEffect(() => {
    setZoom(1);
    setTranslation({ x: 0, y: 0 });
  }, [zoomable, trackSid]);

  const clampZoom = useCallback(
    (value: number) => Math.min(Math.max(value, MIN_ZOOM), MAX_ZOOM),
    [],
  );

  const applyZoom = useCallback(
    (updater: (prev: number) => number) => {
      setZoom((prev) => {
        const next = clampZoom(updater(prev));
        if (next === MIN_ZOOM) setTranslation({ x: 0, y: 0 });
        return next;
      });
    },
    [clampZoom],
  );

  useEffect(() => {
    const el = bgRef.current;
    if (!el || !zoomable) return;
    const onWheel = (ev: WheelEvent): void => {
      ev.preventDefault();
      ev.stopPropagation();
      const deltaPx =
        ev.deltaMode === WheelEvent.DOM_DELTA_LINE
          ? ev.deltaY * WHEEL_LINE_HEIGHT_PX
          : ev.deltaY;
      applyZoom((prev) => prev - deltaPx * WHEEL_ZOOM_COEFFICIENT);
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [zoomable, applyZoom]);

  const canPan = zoomable && zoom > MIN_ZOOM;

  const onBgDoubleClick = useCallback(() => {
    if (!zoomable) return;
    setZoom(MIN_ZOOM);
    setTranslation({ x: 0, y: 0 });
  }, [zoomable]);

  const onBgPointerDown = useCallback(
    (ev: React.MouseEvent) => {
      if (!canPan || ev.button !== 0) return;
      ev.preventDefault();
      dragOrigin.current = {
        pointerX: ev.clientX,
        pointerY: ev.clientY,
        translationX: translation.x,
        translationY: translation.y,
      };
      setPanning(true);
    },
    [canPan, translation],
  );

  const onBgPointerMove = useCallback(
    (ev: React.MouseEvent) => {
      if (!panning) return;
      ev.preventDefault();
      setTranslation({
        x:
          dragOrigin.current.translationX +
          (ev.clientX - dragOrigin.current.pointerX),
        y:
          dragOrigin.current.translationY +
          (ev.clientY - dragOrigin.current.pointerY),
      });
    },
    [panning],
  );

  const onBgPointerUp = useCallback(() => setPanning(false), []);

  const bgStyle = zoomable
    ? {
        transform: `translate(${translation.x}px, ${translation.y}px) scale(${zoom})`,
        transition: panning ? "none" : "transform 150ms ease",
        cursor: canPan ? (panning ? "grabbing" : "grab") : undefined,
      }
    : undefined;

  return (
    <animated.div
      className={classNames(styles.media, className, {
        [styles.mirror]: mirror,
        [styles.crt]: retro,
        [styles.television]: television,
      })}
      // Переменную читает и сам кадр, и подсветка говорящего у плитки — иначе
      // подсветка торчала бы углами из-под выпуклой формы.
      data-retro={retro || undefined}
      style={
        retro
          ? { ...style, ["--klava-crt" as string]: `url(#${clipId})` }
          : style
      }
      ref={ref}
      data-testid="videoTile"
      data-call-local={localParticipant ? "true" : "false"}
      data-call-speaking={(activeSpeaker ?? speaking) ? "true" : "false"}
      data-call-name={displayName}
      data-video-fit={effectiveVideoFit}
      {...props}
    >
      {retro && (
        <svg className={styles.clipDefs} aria-hidden width={0} height={0}>
          <defs>
            <clipPath id={clipId} clipPathUnits="objectBoundingBox">
              <path d={CRT_SHAPE} />
            </clipPath>
          </defs>
        </svg>
      )}
      <div
        className={styles.bg}
        ref={bgRef}
        style={bgStyle}
        onDoubleClick={onBgDoubleClick}
        onMouseDown={onBgPointerDown}
        onMouseMove={onBgPointerMove}
        onMouseUp={onBgPointerUp}
        onMouseLeave={onBgPointerUp}
      >
        <Avatar
          id={member?.userId ?? displayName}
          name={displayName}
          size={avatarSize}
          src={member?.getMxcAvatarUrl()}
          className={styles.avatar}
          style={{ display: video && videoEnabled ? "none" : "initial" }}
        />
        {video?.publication !== undefined && (
          <VideoTrack
            trackRef={video}
            // There's no reason for this to be focusable
            tabIndex={-1}
            disablePictureInPicture
            style={{ display: video && videoEnabled ? "block" : "none" }}
            data-testid="video"
          />
        )}
        {television && videoEnabled && (
          <div
            aria-hidden="true"
            className={styles.scanlines}
            style={{ opacity: Math.max(0, Math.min(0.5, tvIntensity)) }}
          />
        )}
      </div>
      <div className={styles.fg}>
        <div className={styles.reactions}>
          <RaisedHandIndicator
            raisedHandTime={raisedHandTime}
            miniature={avatarSize < 96}
            showTimer={true}
            onClick={raisedHandOnClick}
            tabIndex={focusable ? undefined : -1}
          />
          {currentReaction && (
            <ReactionIndicator
              miniature={avatarSize < 96}
              emoji={currentReaction.emoji}
            />
          )}
        </div>
        {!video && !localParticipant && (
          <div className={styles.status}>
            {t("video_tile.waiting_for_media")}
          </div>
        )}
        {(audioStreamStats || videoStreamStats) && (
          <RTCConnectionStats
            audio={audioStreamStats}
            video={videoStreamStats}
          />
        )}
        <span
          className={styles.connectionQualityAnchor}
          data-testid="tile-top-right"
        >
          <AgendaBadge userId={member?.userId} />
          {showConnectionQualityIndicator && connectionQuality && (
            <span
              role="img"
              aria-label={t("video_tile.connection_quality.aria_label", {
                quality: connectionQualityLabel,
              })}
              title={t("video_tile.connection_quality.tooltip", {
                quality: connectionQualityLabel,
              })}
              className={styles.connectionQuality}
              data-quality={connectionQuality}
              data-testid="connection-quality-indicator"
            >
              <span />
              <span />
              <span />
              <span />
            </span>
          )}
        </span>
        {speaking !== undefined && (
          <span className={styles.equalizerAnchor} data-testid="side-equalizer">
            <SideEqualizer
              active={speaking}
              connectionQuality={connectionQuality}
              miniature={avatarSize < 96}
            />
          </span>
        )}
        {/* TODO: Bring this back once encryption status is less broken */}
        {/*encryptionStatus !== EncryptionStatus.Okay && (
            <div className={styles.status}>
              <Text as="span" size="sm" weight="medium" className={styles.name}>
                {encryptionStatus === EncryptionStatus.Connecting &&
                  t("e2ee_encryption_status.connecting")}
                {encryptionStatus === EncryptionStatus.KeyMissing &&
                  t("e2ee_encryption_status.key_missing")}
                {encryptionStatus === EncryptionStatus.KeyInvalid &&
                  t("e2ee_encryption_status.key_invalid")}
              </Text>
            </div>
          )*/}
        <div className={styles.nameTag}>
          {nameTagLeadingIcon}
          {showLocalRecordingIndicator && (
            <span className={styles.localRecordingDot} aria-hidden />
          )}
          <Text
            as="span"
            size="sm"
            weight="medium"
            className={styles.name}
            data-testid="name_tag"
          >
            {displayName}
          </Text>
          {unencryptedWarning && (
            <Tooltip
              label={t("common.unencrypted")}
              placement="bottom"
              isTriggerInteractive={false}
              nonInteractiveTriggerTabIndex={focusable ? undefined : -1}
            >
              <ErrorSolidIcon
                width={20}
                height={20}
                className={styles.errorIcon}
                role="img"
                aria-label={t("common.unencrypted")}
              />
            </Tooltip>
          )}
        </div>
        {primaryButton}
      </div>
    </animated.div>
  );
};

MediaView.displayName = "MediaView";
